import { useState, useRef } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import { MdCloudUpload } from "react-icons/md";
import { BiSolidFileBlank, BiImage } from "react-icons/bi";
import SpinWheelService from "../../Services/SpinWheelService";
import { toast } from "react-toastify";
import PropertyImageModel from "./PropertyImageModel";

const MediaUpload = ({ mediaType, files, onChange, maxFiles = 5, accept, folderpath }) => {
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [isOpen , setIsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  

  const showErrorToast = (message) => {
    toast.error(message, { position: "top-right", autoClose: 3000 });
  };

  const handleImageUpload = async (file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      const response = await SpinWheelService.createBannerImageTemplate(formData, folderpath);

      if (response?.data?.imageUrl) {
        return response.data.imageUrl;
      }
      throw new Error("Failed to get image URL");
    } catch (error) {
      showErrorToast("File upload failed");
      console.error("File upload error:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length > 0) {
      if (files.length + newFiles.length > maxFiles) {
        showErrorToast(`You can only upload a maximum of ${maxFiles} ${mediaType}`);
        return;
      }

      const validFiles = newFiles.filter((file) => {
        const allowedTypes = accept.split(",");
        return allowedTypes.includes(file.type) || allowedTypes.includes(file.type.split("/")[0] + "/*");
      });

      if (validFiles.length === 0) {
        showErrorToast(`Please upload valid ${mediaType} files`);
        return;
      }

      const uploadedUrls = [];
      for (const file of validFiles) {
        const maxSizeMB = 1;
        if (file.size > maxSizeMB * 1024 * 1024) {
          showErrorToast("File must be less than 1MB.");
          return;
        }
        console.log(`Uploading: ${file.name}, Size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);

        const imageUrl = await handleImageUpload(file);
        if (imageUrl) {
          uploadedUrls.push(imageUrl);
        }
      }

      if (uploadedUrls.length > 0) {
        onChange([...files, ...uploadedUrls]);
      }

      // Reset file input to allow new uploads
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles = droppedFiles.filter((file) => {
        const allowedTypes = accept.split(",");
        return allowedTypes.includes(file.type) || allowedTypes.includes(file.type.split("/")[0] + "/*");
      });

      if (validFiles.length === 0) {
        showErrorToast(`Please drop valid ${mediaType} files`);
        return;
      }

      if (files.length + validFiles.length > maxFiles) {
        showErrorToast(`You can only upload a maximum of ${maxFiles} ${mediaType}`);
        return;
      }

      const uploadedUrls = [];
      for (const file of validFiles) {
        const maxSizeMB = 1;
        if (file.size > maxSizeMB * 1024 * 1024) {
          showErrorToast("File must be less than 1MB.");
          break;
        }

        const imageUrl = await handleImageUpload(file);
        if (imageUrl) {
          uploadedUrls.push(imageUrl);
        }
      }

      if (uploadedUrls.length > 0) {
        onChange([...files, ...uploadedUrls]);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!dragActive) setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleRemove = (index, e) => {
    e.stopPropagation();
    const updatedFiles = files.filter((_, i) => i !== index);
    onChange(updatedFiles);
  };

  const isLimitReached = files.length >= maxFiles;

  const getMediaTypeLabel = (file) => {
    if (typeof file === "string") {
      return {
        icon: <BiImage className="text-blue-500" />,
        label: "Image",
        color: "bg-blue-100 text-blue-700",
      };
    }

    const ext = file?.name?.split(".").pop()?.toUpperCase() || "File";
    return {
      icon: <BiSolidFileBlank className="text-gray-500" />,
      label: ext,
      color: "bg-gray-100 text-gray-700",
    };
  };

  const getPreviewContent = (file) => {
    if (typeof file === "string") {
      return <img src={file} className="w-full h-full object-cover rounded" alt="Preview" />;
    }

    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        {getMediaTypeLabel(file).icon}
      </div>
    );
  };

  const getFileName = (file) => {
    if (!file) return "Unnamed file";

    if (typeof file === "string") {
      try {
        const url = new URL(file);
        return decodeURIComponent(url.pathname.split("/").pop()) || "Image";
      } catch {
        return "Image";
      }
    }

    return file.name || "Unnamed file";
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-medium text-gray-700">
          {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} ({files.length}/{maxFiles})
        </h3>
        <button
          onClick={() => !isLimitReached && fileInputRef.current?.click()}
          disabled={isLimitReached || loading}
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
            ${isLimitReached || loading
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}
          `}
        >
          <MdCloudUpload />
          <span>{loading ? "Uploading..." : "Upload"}</span>
        </button>
      </div>

      <div
        className={`
          border rounded-lg mb-4 transition-all
          ${dragActive ? "border-emerald-500 bg-emerald-50" : "border-dashed border-gray-300"}
          ${isLimitReached || loading ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-emerald-300"}
          ${files.length === 0 ? "p-4" : "p-2"}
        `}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop} // Re-enable drag-and-drop
        onClick={(e) => {
          if (!isLimitReached && !loading && e.target === e.currentTarget) {
            fileInputRef.current?.click();
          }
        }}
      >
        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6">
            <MdCloudUpload className="text-3xl text-emerald-500 mb-2" />
            <p className="text-sm text-gray-600">
              {dragActive ? `Drop ${mediaType} here...` : `Drag & drop ${mediaType} here or click to upload`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {files.map((file, index) => (
              <div  onClick={() => {
                setSelectedImage(file);
                setIsOpen(true);
              }} key={index} className="relative h-24 border rounded overflow-hidden bg-white group">
                {getPreviewContent(file)}
                <div className="absolute top-1 left-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded-sm ${getMediaTypeLabel(file).color}`}>
                    {getMediaTypeLabel(file).label}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1">
                  <p className="text-white text-xs truncate w-full">{getFileName(file)}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleRemove(index, e)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <IoCloseCircleOutline className="text-red-500 text-lg" />
                </button>
              </div>
            ))}
            {!isLimitReached && (
              <div
                className="h-24 border border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => !loading && fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center text-gray-400">
                  <MdCloudUpload className="text-xl" />
                  <span className="text-xs mt-1">Add more</span>
                </div>
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleUpload}
          className="hidden"
          disabled={isLimitReached || loading}
          accept={accept}
        />
      </div>
      <PropertyImageModel
      isOpen={isOpen}
      isClose={()=>{
        setIsOpen(false)
        setSelectedImage("")

      }}
      selectedImage={selectedImage}
      />
    </div>
  );
};

export default MediaUpload;